import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import equal from 'deep-equal';

import {
  realtyStateOwn,
  realtyStateWithRelatives,
} from 'store/anketa/mappers/livingAddressProperty';
import {
  addressToState,
  dadataAddressToState,
  indexToState,
} from 'store/anketa/mappers/abstractAddress';
import { schemaAddressFiasValue } from 'components/modules/Application/validation';
import { isDocumentClassExists } from 'components/modules/MainAnketa/MainAnketa.helpers';
import {
  ADDRESS_CONFIDENCE_ERRORS,
  KEY_COMPONENTS,
} from 'components/modules/MainAnketa/MainAnketa.constants';
import { ADDRESS_DOCUMENTS, NAMES } from './AddressForm.constants';
import { VOID_FUNCTION } from 'constants';

export const useAddressFormPresenter = ({
  persona,
  validate,
  errors,
  confidenceErrors,
  clearConfidenceErrors,
  setFocusAt,
  onChange,
}) => {
  const { documents } = useSelector((state) => state.anketa);

  const [localErrors, setLocalErrors] = useState({});
  const [addressFormValues, setAddressFormValues] = useState(null);
  const [addressLiveFormValues, setAddressLiveFormValues] = useState(null);
  const [livingAddressPropFormValues, setLivingAddressPropFormValues] = useState(null);
  const [showMore, setShowMore] = useState(false);

  const changeHandler = useCallback(
    (formValue) => {
      setAddressFormValues((previousFormValues) => {
        const mergedValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), previousFormValues)
          : { ...previousFormValues, ...formValue };

        if (!formValue.livingAddressEquals) {
          setShowMore(true);
        }

        onChange({
          values: mergedValues,
          id: persona.id,
          key: KEY_COMPONENTS.ADDRESS_REG,
        });

        return mergedValues;
      });
    },
    [persona.id, onChange]
  );

  const changeHandlerAddressLive = useCallback(
    (formValue) => {
      setAddressLiveFormValues((previousFormValues) => {
        const mergedValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), previousFormValues)
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedValues,
          id: persona.id,
          key: KEY_COMPONENTS.ADDRESS_LIVE,
        });

        return mergedValues;
      });
    },
    [persona.id, onChange]
  );

  const changeHandlerLivingAddressProp = useCallback(
    (formValue) => {
      setLivingAddressPropFormValues((previousFormValues) => {
        const mergedValues = Array.isArray(formValue)
          ? formValue.reduce((acc, value) => ({ ...acc, ...value }), previousFormValues)
          : { ...previousFormValues, ...formValue };

        onChange({
          values: mergedValues,
          id: persona.id,
          key: KEY_COMPONENTS.LIVING_ADDRESS_PROPERTY,
        });

        return mergedValues;
      });
    },
    [persona.id, onChange]
  );

  const changeHandlerAddress = useCallback(
    (formValue) => {
      if (formValue.data) {
        const address = dadataAddressToState(formValue.data, formValue.value);

        changeHandler(address);

        schemaAddressFiasValue
          .validate({ ...address }, { abortEarly: false })
          .then(() => {
            if (address.apartmentNumber)
              setFocusAt(`${KEY_COMPONENTS.ADDRESS_REG}_${NAMES.REGISTRATION_DATE}_input`);
          })
          .catch(VOID_FUNCTION);
      } else {
        changeHandler(addressToState(formValue.value));
      }
    },
    [changeHandler]
  );

  const changeHandlerIndex = useCallback(
    (formValue) => {
      changeHandler(indexToState(formValue));
    },
    [changeHandler]
  );

  const changeHandlerIndexLive = useCallback(
    (formValue) => {
      changeHandlerAddressLive(indexToState(formValue));
    },
    [changeHandlerAddressLive]
  );

  const changeHandlerRegistrationDate = useCallback(
    (formValue) => {
      const normalizedValue =
        formValue[NAMES.REGISTRATION_DATE]?.length === 2 ? '' : formValue[NAMES.REGISTRATION_DATE];

      changeHandler({
        [NAMES.REGISTRATION_DATE]: normalizedValue,
      });
    },
    [changeHandler]
  );

  const onHandleAddressLive = useCallback(
    (formValue) => {
      if (formValue.data) {
        changeHandlerAddressLive(dadataAddressToState(formValue.data, formValue.value));
      } else {
        changeHandlerAddressLive(addressToState(formValue.value));
      }
    },
    [changeHandlerAddressLive]
  );

  const changeHandlerLivingAddressEquals = useCallback(() => {
    changeHandler({
      [NAMES.LIVING_ADDRESS_EQUALS]: !addressFormValues[NAMES.LIVING_ADDRESS_EQUALS],
    });

    if (!addressFormValues[NAMES.LIVING_ADDRESS_EQUALS]) {
      setFocusAt(`${KEY_COMPONENTS.ADDRESS_LIVE}_${NAMES.FIAS_VALUE}_input`);
    }
  }, [addressFormValues, changeHandler, setFocusAt]);

  const onHandleLivingAddressProp = useCallback(() => {
    changeHandlerLivingAddressProp({
      [NAMES.REALTY_STATE]:
        livingAddressPropFormValues[NAMES.REALTY_STATE]?.id !== realtyStateOwn.id
          ? realtyStateOwn
          : realtyStateWithRelatives,
    });
  }, [livingAddressPropFormValues, changeHandlerLivingAddressProp]);

  const validation = useCallback(
    (path) => {
      setLocalErrors((prev) => ({ ...prev, [path]: errors[path] || null }));
      clearConfidenceErrors(path);
    },
    [errors, clearConfidenceErrors]
  );

  const getError = useCallback(
    (path) => {
      const validationError = errors[path];
      const generalError = localErrors[path];

      if (!validate) {
        return generalError;
      }

      return validationError ?? generalError;
    },
    [validate, errors, localErrors]
  );

  const getConfidenceError = useCallback(
    (path) => {
      return Object.hasOwn(confidenceErrors, path);
    },
    [confidenceErrors]
  );

  const onHeaderClick = useCallback(() => {
    setShowMore((prev) => !prev);
  }, []);

  const isAddressError = useMemo(() => {
    if (!errors || !localErrors || !confidenceErrors) {
      return false;
    }

    const ADDRESS_FIELDS = [
      KEY_COMPONENTS.ADDRESS_REG,
      KEY_COMPONENTS.ADDRESS_LIVE,
      ADDRESS_CONFIDENCE_ERRORS.FIAS_VALUE,
      ADDRESS_CONFIDENCE_ERRORS.REGISTRATION_DATE,
    ];

    const allErrors = { ...errors, ...localErrors, ...confidenceErrors };

    return Object.keys(allErrors).some(
      (key) => allErrors[key] && ADDRESS_FIELDS.some((field) => key.includes(field))
    );
  }, [errors, localErrors, confidenceErrors]);

  const shouldShowCheckIcon = useMemo(() => {
    return (
      isDocumentClassExists(documents, ADDRESS_DOCUMENTS) &&
      !isAddressError &&
      (addressFormValues?.livingAddressEquals || addressLiveFormValues?.houseNumber)
    );
  }, [documents, isAddressError, addressFormValues, addressLiveFormValues]);

  const shouldShowAlertIcon = useMemo(() => {
    return (
      !isDocumentClassExists(documents, ADDRESS_DOCUMENTS) ||
      isAddressError ||
      !addressFormValues?.livingAddressEquals ||
      !addressLiveFormValues?.houseNumber
    );
  }, [documents, isAddressError, addressFormValues, addressLiveFormValues]);

  const shouldShowIndex = useMemo(() => {
    return (
      !addressFormValues?.isDadataIndexValue &&
      addressFormValues?.fiasValue &&
      addressFormValues?.houseNumber &&
      addressFormValues?.isDadataValue
    );
  }, [addressFormValues]);

  const shouldShowIndexLive = useMemo(() => {
    return (
      !addressLiveFormValues?.isDadataIndexValue &&
      addressLiveFormValues?.fiasValue &&
      addressLiveFormValues?.houseNumber &&
      addressLiveFormValues?.isDadataValue
    );
  }, [addressLiveFormValues]);

  useEffect(() => {
    if (!persona || equal(addressFormValues, persona[KEY_COMPONENTS.ADDRESS_REG])) {
      return;
    }

    setAddressFormValues(persona[KEY_COMPONENTS.ADDRESS_REG]);
  }, [persona]);

  useEffect(() => {
    if (!persona || equal(addressFormValues, persona[KEY_COMPONENTS.ADDRESS_LIVE])) {
      return;
    }

    setAddressLiveFormValues(persona[KEY_COMPONENTS.ADDRESS_LIVE]);
  }, [persona]);

  useEffect(() => {
    if (!persona || equal(addressFormValues, persona[KEY_COMPONENTS.LIVING_ADDRESS_PROPERTY])) {
      return;
    }

    setLivingAddressPropFormValues(persona[KEY_COMPONENTS.LIVING_ADDRESS_PROPERTY]);
  }, [persona]);

  useEffect(() => {
    if (validate) {
      return;
    }

    setLocalErrors(
      Object.keys(localErrors).reduce((obj, key) => ({ ...obj, [key]: errors[key] || null }), {})
    );
  }, [errors, validate]);

  useEffect(() => {
    if (
      documents &&
      isDocumentClassExists(documents, ADDRESS_DOCUMENTS) &&
      addressFormValues &&
      (errors || localErrors || confidenceErrors)
    ) {
      if (isAddressError) {
        setShowMore(true);
      }
    }
  }, [isAddressError]);

  return {
    showMore,
    shouldShowIndex,
    addressFormValues,
    shouldShowIndexLive,
    shouldShowAlertIcon,
    shouldShowCheckIcon,
    addressLiveFormValues,
    livingAddressPropFormValues,
    getError,
    validation,
    onHeaderClick,
    changeHandlerIndex,
    getConfidenceError,
    onHandleAddressLive,
    changeHandlerAddress,
    changeHandlerIndexLive,
    onHandleLivingAddressProp,
    changeHandlerRegistrationDate,
    changeHandlerLivingAddressProp,
    changeHandlerLivingAddressEquals,
  };
};
